import { useEffect, useState } from "react";
import { Building2, Loader2, Search, ChevronRight, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useDebounce } from "@/hooks/useDebounce";
import { searchBrreg } from "@/lib/geo";
import { cn } from "@/lib/utils";

export const formatOrgNumber = (n) => {
  const d = String(n || "").replace(/\D/g, "");
  return d.length === 9 ? `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}` : n || "";
};

/**
 * BrregSearch – søk i Enhetsregisteret (Brønnøysund) på firmanamn eller org.nr.
 * Viser treff inline; brukaren må aktivt velje eit treff (eksplisitt stadfesting) før felt blir fylt.
 */
export const BrregSearch = ({ onPick, testid = "brreg-search", autoFocus }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const debounced = useDebounce(q, 320);

  useEffect(() => {
    const t = debounced.trim();
    if (t.length < 2) {
      setResults([]);
      setError(null);
      return;
    }
    let cancelled = false;
    setBusy(true);
    setError(null);
    searchBrreg(t, 8)
      .then((r) => !cancelled && setResults(r))
      .catch(() => !cancelled && setError("Fekk ikkje kontakt med Brønnøysundregistera. Prøv igjen, eller fyll inn manuelt."))
      .finally(() => !cancelled && setBusy(false));
    return () => {
      cancelled = true;
    };
  }, [debounced]);

  const isOrg = /^\d[\d\s]{7,}$/.test(q.trim());
  const showEmpty = !busy && !error && debounced.trim().length >= 2 && results.length === 0;

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Firmanamn eller org.nr (9 siffer)"
          autoFocus={autoFocus}
          autoComplete="off"
          inputMode={isOrg ? "numeric" : "text"}
          className="h-11 pl-9 pr-9"
          data-testid={`${testid}-input`}
        />
        {busy && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />}
      </div>

      {error && (
        <p className="flex items-start gap-1.5 text-xs text-[rgb(var(--status-overdue))]" data-testid={`${testid}-error`}>
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {error}
        </p>
      )}
      {showEmpty && (
        <p className="text-xs text-muted-foreground" data-testid={`${testid}-empty`}>
          Ingen treff i Enhetsregisteret. Sjekk stavemåte/org.nr – eller fyll inn kunden manuelt under.
        </p>
      )}

      {results.length > 0 && (
        <ul className="max-h-72 overflow-y-auto rounded-xl border bg-card p-1 shadow-sm" role="listbox" data-testid={`${testid}-results`}>
          {results.map((r, i) => {
            const taken = !!r.existing_customer_id;
            return (
              <li key={r.org_number || i} role="option" aria-selected="false">
                <button
                  type="button"
                  onClick={() => onPick?.(r)}
                  className={cn(
                    "pressable flex w-full items-center gap-3 rounded-lg px-2.5 py-2.5 text-left transition-colors hover:bg-accent",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                  )}
                  data-testid={`${testid}-option-${r.org_number || i}`}
                >
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-muted">
                    <Building2 className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold">{r.name}</span>
                      {r.bankrupt && <span className="shrink-0 rounded-full border border-[rgba(217,45,32,0.25)] bg-[rgba(217,45,32,0.12)] px-1.5 text-[10px] font-semibold text-[rgb(var(--status-overdue))]">Konkurs</span>}
                      {taken && <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-muted px-1.5 text-[10px] font-semibold text-muted-foreground"><CheckCircle2 className="h-3 w-3" /> Allereie kunde</span>}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      Org.nr {formatOrgNumber(r.org_number)}{r.org_form ? ` · ${r.org_form}` : ""}{r.address ? ` · ${r.address}` : ""}
                    </span>
                    {r.industry && <span className="block truncate text-[11px] text-muted-foreground/80">{r.industry}</span>}
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              </li>
            );
          })}
          <li className="px-2.5 pb-1 pt-1.5 text-[11px] text-muted-foreground">Kjelde: Enhetsregisteret (Brønnøysundregistera)</li>
        </ul>
      )}
    </div>
  );
};
